/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";

import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory, useParams, useLocation, Prompt } from "react-router-dom";

import { PRIVATE_ROUTES } from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import useActionAutomaticSequence from "../../../hooks/useActionAutomaticSequence";
import useActionShipment from "../../../hooks/useActionShipment";
import { ContainerStatus, ContainerPosition } from "../../../models/marine.traffic";
import { PurchaseOrder } from "../../../models/purchase.order";
import { ShipmentDocument } from "../../../models/shipment.document";
import { ReceivedProduct, Shipment } from "../../../models/shipments";
import ShipmentBadgeStatus from "../../../pages/private/SupplyChainPlus/Shipments/components/StatusBadge";
import MarineTrafficService from "../../../services/marine.traffic.service";
import {
  capitalizeFirstLetter,
  capitalizeFirstLetterAllWorlds,
} from "../../../util/capitalizeFirstLetter";
import {
  AutomaticSequenceOptions,
  CountryCode,
  CountryName,
  ShipmentDetails,
  ShipmentStatus,
  ShipmentType,
} from "../../../util/enums";
import useActionBooking from "../../Booking/actions";
import { useBookingFiles } from "../../Booking/hooks/useBookingFiles";
import { useDocuments } from "../../Booking/hooks/useDocuments";
import { convertPurchaseOrdersToPurchaseOrdersProducts } from "../../Booking/Maintenance/actions";
import {
  calculateBookingCartonTotals,
  calculatePurchaseOrderTotal,
  calculateScannedItemsFromBooking,
} from "../../Booking/Maintenance/util";
import { BookingTabs, ShipmentObjects } from "../../Booking/util";
import BadgeShipmentTrackingStatus from "../../Generic/BadgeShipmentTrackingStatus";
import Breadcrumbs from "../../Generic/Breadcrump";
import CartonSSCC from "../../Generic/CartonSSCC";
import { CartonSSCCFrom } from "../../Generic/CartonSSCC/util";
import History, { Item, SkeletonHistory } from "../../Generic/History";
import UITabMaterial, { PanelProps } from "../../Generic/UITabMaterial";
import TitlePage from "../../TitlePage";
import { VoyageDetails } from "Components/Tracking";
import SkeletonTracking from "../../Tracking/skeleton";
import {
  ShipmentOrdersRecourseProvider,
  useShipmentOrdersRecourse,
} from "../context/AvailableOrders";
import { ErrorsProvider } from "../context/Errors";
import { ShipmentResourcesProvider } from "../context/Resources";
import { ShipmentProvider } from "../context/Shipment";
import { useErrorsContext } from "../hooks/useErrorsContext";
import { useShipmentResourcesContext } from "../hooks/useResourceContext";
import { useShipmentContext } from "../hooks/useShipmentContext";
import { ShipmentKey } from "../types";
import { ShipmentTabs } from "../utils";
import ShipmentActionButtons from "./components/ShipmentActionButtons";
import CreateShipmentConfirmationModal, {
  CreateShipmentConfirmationModalHandles,
} from "./CreateShipmentConfirmationModal";
import Details, { SkeletonDetails } from "./Details";
import Documents from "./Documents";
import SkeletonDocuments from "./Documents/skeleton";
import ShipmentPuchaseOrders from "./Orders";
import SkeletonPurchaseOrder from "./Orders/skeleton";
import useStyles from "./styles";
import { ShipmentHistory } from "Models/history";

interface LocationProps {
  viewOnly: boolean;
  shipment: Shipment;
}

const ShipmentMaintenanceScreen = () => {
  const { shipmentId } = useParams<{ shipmentId?: string | undefined }>();
  const { state } = useLocation<LocationProps>();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const history = useHistory();
  const classes = useStyles();

  const createShipmentConfirmationRef = useRef<CreateShipmentConfirmationModalHandles>(null);

  const [viewOnly, setViewOnly] = useState<boolean>(false);

  const [containerStatus, setContainerStatus] = useState<ContainerStatus>();
  const [receivedProducts, setReceivedProducts] = useState<ReceivedProduct[]>();
  const [leftPanelSelected, setLeftPanelSelected] = useState<string>(ShipmentTabs.DETAILS);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [containerPosition, setContainerPosition] = useState<ContainerPosition>();

  const {
    shipment,
    setShipment,
    handleUpdateShipment,
    histories,
    rootContainerId,
    handleChangeOptions,
    hasUnsavedChanges,
    sequence,
    setSequence,
    setHasUnsavedChanges,
  } = useShipmentContext();
  const onChangeDocuments = (documentsClone: ShipmentDocument[]) => {
    setShipment((prev) => ({ ...prev, documents: documentsClone }));
  };
  const { documentsJoinned } = useDocuments({ documents: shipment?.documents });
  const {
    onDownloadDocument,
    onDownloadDocumentCsv,
    onDownloadAllDocuments,
    loadingSavingCsvDocument,
    loadingAllDocuments,
  } = useBookingFiles({
    bookingId: shipment?.bookingId || shipment?.id,
    hasBookingId: !!shipment?.bookingId,
    documents: shipment?.documents,
    onChangeDocuments,
    setHasUnsavedChanges,
  });

  const {
    companyDocuments,
    loading: loadingResources,
    resourceCompanyId,
  } = useShipmentResourcesContext();

  const { isLoading: loadingAvailableOrders } = useShipmentOrdersRecourse();
  const { getCartonsByBooking, getOrdersByBooking } = useActionBooking();
  const { getAutomaticSequenceByType } = useActionAutomaticSequence();

  const { isFetching: loadingSequence } = getAutomaticSequenceByType(
    AutomaticSequenceOptions.SHIPMENT,
    !shipmentId,
    setSequence
  );

  const isCreate = useMemo(() => !viewOnly && !shipmentId, [viewOnly, shipmentId]);

  useEffect(() => {
    if (!shipmentId) {
      history.replace(PRIVATE_ROUTES.SHIPMENTS);
    }
  }, [history, shipmentId]);

  useEffect(() => {
    if (sequence && !shipment?.containerId) {
      const value = `${sequence?.prefix ?? ""}${sequence?.nextValue ?? ""}`;

      handleUpdateShipment(ShipmentDetails.CONTAINER_ID, value);
    }
  }, [sequence]);

  const { data: cartonsShipmentData, isInitialLoading: loadingCartons } = getCartonsByBooking(
    Number(shipment?.bookingId || shipmentId)
  );
  const { data: ordersShipmentData, isInitialLoading: loadingOrders } = getOrdersByBooking(
    Number(shipment?.bookingId || shipmentId)
  );

  useEffect(() => {
    if (state) {
      setViewOnly(state.viewOnly);
      if (state?.shipment) {
        setShipment(state?.shipment || shipment);
      }
    }
  }, [state]);

  useEffect(() => {
    if (cartonsShipmentData) {
      setShipment((prev) => ({
        ...prev,
        ...calculateBookingCartonTotals(cartonsShipmentData),
      }));
    }
  }, [cartonsShipmentData]);

  useEffect(() => {
    if (ordersShipmentData && cartonsShipmentData) {
      setShipment((prev) => ({
        ...prev,
        purchaseOrders: calculatePurchaseOrderTotal(ordersShipmentData, cartonsShipmentData),
      }));
    }
  }, [ordersShipmentData, cartonsShipmentData]);

  const resourceOrders = useMemo(() => shipment?.purchaseOrders || [], [shipment?.purchaseOrders]);
  const resourceProducts = useMemo(
    () => convertPurchaseOrdersToPurchaseOrdersProducts(shipment?.purchaseOrders || []),
    [shipment?.purchaseOrders]
  );

  const { handleValidate, someFieldHasError } = useErrorsContext();

  const isDischargePortFromBrazil = useMemo(() => {
    const countryCode = shipment?.portOfDischarge?.country?.code;

    return countryCode === CountryCode.BRAZIL;
  }, [shipment?.portOfDischarge, viewOnly]);

  const ORIGIN_CRITERION_ORIGIN_COUNTRIES = [
    CountryCode.BANGLADESH,
    CountryCode.INDIA,
    CountryCode.CAMBODIA,
    CountryCode.INDONESIA,
    CountryCode.PAKISTAN,
  ];

  const showOriginCriterion = useMemo(() => {
    const hasQualifyingOrigin = shipment?.purchaseOrders?.some((po) => {
      const country = po.supplier?.location?.country;
      return (
        !!country &&
        (ORIGIN_CRITERION_ORIGIN_COUNTRIES.includes(country as CountryCode) ||
          Object.values(CountryName).includes(country as CountryName))
      );
    });

    const destinationCode =
      shipment?.portOfDestination?.country?.code ?? shipment?.portOfDischarge?.country?.code;

    return !!hasQualifyingOrigin && destinationCode === CountryCode.UNITED_KINGDOM;
  }, [shipment?.purchaseOrders, shipment?.portOfDestination, shipment?.portOfDischarge]);

  const { getShipmentById, getShipmentHistories, useSaveShipment, useCancelShipment } =
    useActionShipment();

  const { mutate: sendShipment, isLoading: loadingUpdateShipment } = useSaveShipment(() => {
    setHasUnsavedChanges(false);
    if (isCreate) {
      createShipmentConfirmationRef?.current?.open();
    } else {
      history.push(PRIVATE_ROUTES.SHIPMENTS);
    }
  });

  const { mutate: cancelShipment, isLoading: cancellingShipment } = useCancelShipment();

  const { isFetching: fetchingShipment, refetch: refetchShipment } = getShipmentById(
    Number(shipmentId)
  );

  const { isFetching: fetchingHistories, data: historyData } = getShipmentHistories(
    Number(shipmentId)
  );

  const isUpdatingAction: boolean = cancellingShipment || loadingUpdateShipment;

  const handleRemovePurchaseOrder = useCallback(
    (orderToRemove: PurchaseOrder) => {
      const ordersUpdated =
        shipment?.purchaseOrders?.filter((order) => order.id !== orderToRemove.id) ?? [];
      const cartonsUpdated =
        shipment?.cartons?.filter((carton) => carton.purchaseOrderId !== orderToRemove.id) ?? [];

      handleUpdateShipment("purchaseOrders", ordersUpdated);
      handleChangeOptions(ShipmentObjects.PURCHASE_ORDERS);

      handleUpdateShipment("cartons", cartonsUpdated);
      handleChangeOptions(ShipmentObjects.CARTONS);
    },
    [shipment?.purchaseOrders, shipment?.cartons]
  );

  const validateAndSaveShipment = () => {
    if (!handleValidate()) return;
    if (!shipment) return;
    sendShipment({ id: shipment?.id, shipment });
  };

  const getItemsToHistory = () => {
    if (!shipmentId) {
      const firstHistoryIp = (
        histories.find((history: ShipmentHistory) => history?.userIpAddress)?.userIpAddress ?? ""
      ).trim();
      return [
        {
          title: "Shipment created",
          activityDate: `${format(new Date(), "yyyy/MM/dd hh:mmbb")}`,
          activityUser: `${currentUser?.name} ${currentUser?.lastName}`,
          activityUserIp: firstHistoryIp,
        } as Item,
      ];
    }

    if (histories && histories.length > 0) {
      return histories.map((history: ShipmentHistory) => {
        const user = history?.user
          ? `${history?.user?.name} ${history?.user?.lastName ? history?.user?.lastName : ""}`
          : "";

        return {
          title: capitalizeFirstLetter(history?.action ?? ""),
          activityDate: history?.updatedAt
            ? `${format(new Date(history?.updatedAt), "yyyy/MM/dd hh:mmbb")}`
            : "",
          activityUser: user,
          activityUserIp: history.userIpAddress ?? "",
        } as Item;
      });
    }
  };

  const onCancel = () => {
    if (history.action !== "POP") {
      history.goBack();
    } else {
      history.replace(PRIVATE_ROUTES.SHIPMENTS);
    }
  };

  const onUpdateShipment = (attribute: ShipmentKey, value: any, noChanges?: boolean) => {
    if (!noChanges) setHasUnsavedChanges(true);
    handleUpdateShipment(attribute, value);
  };

  const detailsPanel: PanelProps = {
    key: "details",
    menuItem: translate("details"),
    className: classes.uiTab,
    loading: fetchingShipment || loadingSequence,
    hasError: someFieldHasError(Object.values(ShipmentDetails)),
    skeleton: () => <SkeletonDetails />,
    render: () => (
      <Details
        cancelShipment={cancelShipment}
        viewOnly={{
          fields: viewOnly ?? false,
          actions: viewOnly ?? false,
        }}
        showDeleteConfirmation={showDeleteConfirmation}
        setShowDeleteConfirmation={setShowDeleteConfirmation}
      />
    ),
  };
  const ordersPanel: PanelProps = {
    key: BookingTabs.ORDERS,
    menuItem: translate(BookingTabs.ORDERS),
    className: classes.uiTab,
    loading: loadingCartons || loadingOrders || fetchingShipment,
    skeleton: () => <SkeletonPurchaseOrder />,
    render: () => (
      <ShipmentPuchaseOrders
        purchaseOrders={shipment?.purchaseOrders || []}
        onAddPurchaseOrder={(orders) => {
          onUpdateShipment("purchaseOrders", orders);
          handleChangeOptions(ShipmentObjects.PURCHASE_ORDERS, true);
        }}
        onRemovePurchaseOrder={handleRemovePurchaseOrder}
        viewOnly={{
          fields: viewOnly || !!shipment?.bookingId || !!shipment?.shipmentTrackingId,
          actions: viewOnly || !!shipment?.bookingId || !!shipment?.shipmentTrackingId,
        }}
        loadingOrders={loadingOrders}
        fromMaintenance={true}
      />
    ),
  };

  const ssccPanel: PanelProps = {
    menuItem: translate("sscc"),
    className: classes.uiTab,
    key: "sscc",
    loading: fetchingShipment || loadingOrders || loadingCartons,
    skeleton: () => <SkeletonPurchaseOrder />,
    render: () => (
      <CartonSSCC
        cartons={shipment?.cartons || []}
        updateCartons={(cartons) => {
          onUpdateShipment("cartons", cartons);
          handleChangeOptions(ShipmentObjects.CARTONS);
        }}
        onDeleteSSCC={(ssccs?: string[]) => {
          const cartons = cloneDeep(shipment?.cartons);

          if (ssccs && cartons) {
            for (const sscc of ssccs) {
              const index = cartons?.findIndex((carton) => carton.sscc === sscc);

              if (index > -1) cartons?.splice(index, 1);
            }
          }

          onUpdateShipment("cartons", cartons);
          handleChangeOptions(ShipmentObjects.CARTONS);
        }}
        loadingCartons={false}
        viewOnly={{
          fields: viewOnly || !!shipment?.shipmentTrackingId,
          actions:
            viewOnly || isUpdatingAction || !!shipment?.bookingId || !!shipment?.shipmentTrackingId,
        }}
        calledFrom={CartonSSCCFrom.SHIPMENT}
        subtitle={{
          quantity: calculateScannedItemsFromBooking(shipment),
          text: translate("scannedItems"),
        }}
        viewSupplier={true}
        booking={shipment}
        resources={{
          purchaseOrders: resourceOrders,
          products: resourceProducts,
          resourceCompanyId,
        }}
        loadingProducts={loadingAvailableOrders}
        isLengthMandatory={isDischargePortFromBrazil}
        showOriginCriterion={showOriginCriterion}
        statusBooking={shipment?.statusShipment}
      />
    ),
    "data-cy": "sscc-tab",
  };

  const documentsPanel = {
    key: BookingTabs.DOCUMENTS,
    menuItem: translate(BookingTabs.DOCUMENTS),
    className: classes.uiTab,
    loading: fetchingShipment || loadingResources,
    isEmpty: !documentsJoinned,
    isOpened: false,
    render: () => (
      <Documents
        companyDocuments={companyDocuments}
        loadingResources={loadingResources}
        shipmentDocuments={documentsJoinned}
        viewOnly={false}
        loadingShipment={fetchingShipment}
        onDownloadDocument={shipment?.bookingId ? onDownloadDocument : undefined}
        onDownloadDocumentCsv={onDownloadDocumentCsv}
        loadingAllDocuments={loadingAllDocuments}
        onDownloadAllDocuments={onDownloadAllDocuments}
        onUpdateShipment={(attribute, value) => {
          onUpdateShipment(attribute, value);
          handleChangeOptions(ShipmentObjects.DOCUMENTS);
        }}
        loadingUploadCsvDocument={loadingSavingCsvDocument}
      />
    ),
    skeleton: () => <SkeletonDocuments />,
  };

  const isShipmentTrackingEnabled =
    !!shipment?.shipmentTrackingId &&
    (shipment?.shipmentType === ShipmentType.SEA || shipment?.shipmentType === ShipmentType.AIR) &&
    shipment?.shipmentTracking?.shipmentStatus?.toLowerCase() !==
      ShipmentStatus.WAITING_FOR_UPDATE.toLowerCase();

  const { isFetching: isFetchingContainerStatus } = useQuery(
    [`container.status.${shipment?.id}`, shipment?.marineTrafficId],
    () =>
      MarineTrafficService.status(
        shipment?.marineTrafficId || shipment?.airwayBill || "",
        shipment?.shipmentType
      ),
    {
      enabled: isShipmentTrackingEnabled,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      retry: false,
      onSuccess: (response) => {
        if (!response.data) setContainerStatus(undefined);
        if (response && response.data) {
          setContainerStatus(response.data);
          const position = {
            TIMESTAMP: shipment?.shipmentTracking?.updatedAt,
            LAT: shipment?.shipmentTracking?.vesselPosition?.latitude ?? "",
            LON: shipment?.shipmentTracking?.vesselPosition?.longitude ?? "",
          } as unknown as ContainerPosition;

          setContainerPosition(position);
        }
      },
    }
  );

  const tracking = {
    key: BookingTabs.TRACKING,
    className: classes.uiTabTracking,
    loading: fetchingShipment || isFetchingContainerStatus,
    isEmpty: !containerStatus,
    menuItem: translate(BookingTabs.TRACKING),
    skeleton: () => <SkeletonTracking isFromMaintenance />,
    render: () => (
      <VoyageDetails
        isCreate={isCreate}
        isFromMaintenance
        shipment={shipment}
        containerPosition={containerPosition}
        containerStatus={containerStatus}
        isOnViewComponent
        viewLivePosition={process.env.NODE_DATABASE !== "work"}
      />
    ),
  };

  const leftPanels: PanelProps[] = [detailsPanel, ordersPanel, ssccPanel, documentsPanel, tracking];

  const historyMemoPanel = useMemo(
    () => <History items={getItemsToHistory()} isModal />,
    [histories]
  );

  const activityPanel: PanelProps = {
    menuItem: translate("activity"),
    key: "history",
    render: () => <History items={getItemsToHistory()} isModal />,
  };

  const activityPanels = [activityPanel];

  const getSubtitle = () => {
    if (viewOnly) {
      return translate("viewShipment");
    }
    if (shipmentId) {
      return translate("editShipment");
    }

    return translate("newShipment");
  };

  if (!shipmentId) {
    return null;
  }

  return (
    <div className={classes.maintenanceDiv}>
      <TitlePage
        left={
          <Breadcrumbs
            items={[
              {
                title: translate("shipment"),
                link: {
                  url: PRIVATE_ROUTES.SHIPMENTS,
                },
              },
              {
                title: getSubtitle(),
                color: "#465869",
              },
            ]}
          />
        }
        right={
          <Grid item xs={12} sm={6} md={6} lg={6} className={classes.titleDataContainer}>
            {shipment?.statusShipment && shipment?.shipmentTracking?.shipmentStatus && (
              <BadgeShipmentTrackingStatus
                value={capitalizeFirstLetterAllWorlds(shipment?.shipmentTracking?.shipmentStatus)}
                className={classes.titleStatusBadge}
              />
            )}
            {shipment?.statusShipment && !shipment?.shipmentTracking?.shipmentStatus && (
              <ShipmentBadgeStatus
                value={capitalizeFirstLetterAllWorlds(shipment.statusShipment)}
                className={classes.titleStatusBadge}
              />
            )}
            <span className={classes.titleData}>
              {shipment?.containerId ? ` ${translate("shipment")} #${shipment?.containerId}` : ""}
            </span>
          </Grid>
        }
      />
      <Prompt when={hasUnsavedChanges} message="" />
      <CreateShipmentConfirmationModal ref={createShipmentConfirmationRef} />
      <div className={classes.shipmentTabs}>
        <UITabMaterial
          leftPanels={leftPanels}
          showRightPanel={false}
          showActivityButton
          activityPanels={activityPanels}
          leftDataCy="left-shipment-tabs"
          rightDataCy="right-shipment-tabs"
          onChangeLeftPanel={(event, panel, key) => key && setLeftPanelSelected(key)}
          tabFooter={
            <ShipmentActionButtons
              onSave={validateAndSaveShipment}
              onCancel={onCancel}
              loading={isUpdatingAction || cancellingShipment}
              id={shipment?.id}
              viewOnly={viewOnly}
              showModal={setShowDeleteConfirmation}
              leftPanelSelected={leftPanelSelected}
            />
          }
        />
      </div>
    </div>
  );
};

export const ShipmentMaintenancePage = () => {
  return (
    <ShipmentProvider>
      <ShipmentResourcesProvider>
        <ShipmentOrdersRecourseProvider>
          <ErrorsProvider>
            <ShipmentMaintenanceScreen />
          </ErrorsProvider>
        </ShipmentOrdersRecourseProvider>
      </ShipmentResourcesProvider>
    </ShipmentProvider>
  );
};
